// Client-side mirror of helpers/budgetCalc.js so the total updates live as the
// user types. The backend recomputes authoritatively on save.

const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const addDaysUTC = (date, days) => {
    const d = new Date(date.getTime());
    d.setUTCDate(d.getUTCDate() + days);
    return d;
};

const dow = (date) => date.getUTCDay();

// Day-by-day projected schedule (like the "Piano" sheet). Returns one row per
// deployed day from start until the target completes (capped).
export const buildSchedule = (project = {}) => {
    const rows = [];
    const totalBuses = toNum(project.total_buses);
    const busesDone = toNum(project.buses_done);
    const targetMonFri = toNum(project.target_mon_fri);
    const targetSat = toNum(project.target_sat);
    const workOnHolidays = !!project.work_on_holidays;
    const remaining = Math.max(0, totalBuses - busesDone);

    const start = project.start_date ? new Date(`${project.start_date}T00:00:00Z`) : null;
    if (!start || isNaN(start.getTime()) || remaining === 0) return rows;
    if (!(targetMonFri > 0 || targetSat > 0 || workOnHolidays)) return rows;

    const MAX_DAYS = 2000;
    let produced = 0;
    for (let i = 0; i < MAX_DAYS; i++) {
        const day = addDaysUTC(start, i);
        const d = dow(day);
        const isSunday = d === 0;
        const isSaturday = d === 6;

        let dailyTarget = 0;
        let deployed = false;
        let dayType;
        if (workOnHolidays) { dailyTarget = isSaturday ? targetSat : targetMonFri; deployed = true; dayType = 'working'; }
        else if (isSunday) { deployed = false; dayType = 'sunday'; }
        else if (isSaturday) { dailyTarget = targetSat; deployed = targetSat > 0; dayType = 'saturday'; }
        else { dailyTarget = targetMonFri; deployed = true; dayType = 'working'; }

        produced += dailyTarget;
        const cumulative = Math.min(totalBuses, busesDone + produced);
        const residual = Math.max(0, totalBuses - cumulative);

        if (deployed || dailyTarget > 0) {
            rows.push({
                date: day.toISOString().slice(0, 10),
                weekday: day.getUTCDay(),
                dayType,
                dailyTarget,
                cumulative,
                residual,
            });
        }
        if (produced >= remaining) break;
    }
    return rows;
};

export const computeBudget = (project = {}) => {
    const totalBuses = toNum(project.total_buses);
    const busesDone = toNum(project.buses_done);
    const targetMonFri = toNum(project.target_mon_fri);
    const targetSat = toNum(project.target_sat);
    const workOnHolidays = !!project.work_on_holidays;

    const costMeal = toNum(project.cost_meal);
    const costLodging = toNum(project.cost_lodging);
    const costExternal = toNum(project.cost_external);
    const employeeDailyCost = toNum(project.employee_daily_cost);
    const numEmployees = toNum(project.num_employees);
    const numExternals = toNum(project.num_externals);

    const remainingBuses = Math.max(0, totalBuses - busesDone);

    let workingDays = 0;
    let completionDate = null;
    let completable = false;

    const startStr = project.start_date;
    const start = startStr ? new Date(`${startStr}T00:00:00Z`) : null;

    if (start && !isNaN(start.getTime())) {
        let produced = 0;
        const MAX_DAYS = 2000;
        if (remainingBuses === 0) {
            completable = true;
            completionDate = startStr;
        } else if (targetMonFri > 0 || targetSat > 0 || workOnHolidays) {
            for (let i = 0; i < MAX_DAYS; i++) {
                const day = addDaysUTC(start, i);
                const d = dow(day);
                const isSunday = d === 0;
                const isSaturday = d === 6;

                let dailyTarget = 0;
                let deployed = false;
                if (workOnHolidays) {
                    dailyTarget = isSaturday ? targetSat : targetMonFri;
                    deployed = true;
                } else if (isSunday) {
                    deployed = false;
                } else if (isSaturday) {
                    dailyTarget = targetSat;
                    deployed = targetSat > 0;
                } else {
                    dailyTarget = targetMonFri;
                    deployed = true;
                }

                if (deployed) workingDays += 1;
                produced += dailyTarget;

                if (produced >= remainingBuses) {
                    completable = true;
                    completionDate = day.toISOString().slice(0, 10);
                    break;
                }
            }
        }
    }

    const employeeCostPerDay = numEmployees * (costMeal + costLodging + employeeDailyCost);
    const externalCostPerDay = numExternals * costExternal;
    const laborCost = workingDays * (employeeCostPerDay + externalCostPerDay);

    const materials = Array.isArray(project.materials) ? project.materials : [];
    const materialsBreakdown = materials.map((m) => {
        const net = toNum(m.unit_price) * toNum(m.quantity);
        const vat = net * (toNum(m.vat_rate) / 100);
        return { net, vat };
    });
    const materialsNet = materialsBreakdown.reduce((s, m) => s + m.net, 0);
    const materialsVat = materialsBreakdown.reduce((s, m) => s + m.vat, 0);
    const materialsTotal = materialsNet + materialsVat;

    const flights = Array.isArray(project.flights) ? project.flights : [];
    const flightsTotal = flights.reduce((s, f) => s + toNum(f.amount), 0);
    const hotels = Array.isArray(project.hotels) ? project.hotels : [];
    const hotelsTotal = hotels.reduce((s, h) => s + toNum(h.amount), 0);
    const travelTotal = flightsTotal + hotelsTotal;
    const grandTotal = laborCost + materialsTotal + travelTotal;

    const round2 = (n) => Math.round(n * 100) / 100;

    return {
        remainingBuses,
        workingDays,
        completable,
        completionDate,
        costs: {
            employeeCostPerDay: round2(employeeCostPerDay),
            externalCostPerDay: round2(externalCostPerDay),
            laborCost: round2(laborCost),
            materialsNet: round2(materialsNet),
            materialsVat: round2(materialsVat),
            materialsTotal: round2(materialsTotal),
            flightsTotal: round2(flightsTotal),
            hotelsTotal: round2(hotelsTotal),
            travelTotal: round2(travelTotal),
            grandTotal: round2(grandTotal),
        },
    };
};
